import { Modal } from "@msys/ui";
import { useTranslate } from "@tolgee/react";
import { Form, Formik, FormikProps } from "formik";
import { uniqueId } from "lodash-es";
import { useSnackbar } from "notistack";
import React from "react";
import {
  useOrganisationMembershipInvitation,
  type OrganisationMembershipInvitationEmailFormValues,
} from "./useOrganisationMembershipInvitation.js";

export type { OrganisationMembershipInvitationEmailFormValues } from "./useOrganisationMembershipInvitation.js";

interface Props {
  title?: string;
  handleClose: () => void;
  handleComplete?: () => Promise<void>;
  refetchQueries: string[];
  userId: string;
}

export const InviteOrganisationMemberModal = ({
  title,
  handleClose,
  handleComplete,
  refetchQueries,
  userId,
}: Props) => {
  const { t } = useTranslate(["OrganisationInvite", "UserProfile", "Global"]);
  const { enqueueSnackbar } = useSnackbar();

  const {
    initialValues,
    validationSchema,
    isLoading,
    formFields,
    createSubmitHandler,
  } = useOrganisationMembershipInvitation({ refetchQueries });

  const onSubmit = React.useCallback(
    async (values: OrganisationMembershipInvitationEmailFormValues) => {
      try {
        const handleSubmit = createSubmitHandler(userId);
        await handleSubmit(values);
        if (handleComplete) {
          await handleComplete();
        }
        enqueueSnackbar(t("Member invited", { ns: "OrganisationInvite" }), {
          variant: "success",
        });
      } catch (error) {
        enqueueSnackbar(
          error instanceof Error ? error.message : t("Error", { ns: "Global" }),
          {
            variant: "error",
          }
        );
      } finally {
        handleClose();
      }
    },
    [
      createSubmitHandler,
      enqueueSnackbar,
      handleClose,
      handleComplete,
      t,
      userId,
    ]
  );

  const formId = React.useMemo(() => uniqueId(), []);

  return (
    <Formik<OrganisationMembershipInvitationEmailFormValues>
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {(
        formikProps: FormikProps<OrganisationMembershipInvitationEmailFormValues>
      ) => (
        <Modal
          id="invite-ogranisation-member-modal"
          title={
            title ??
            t("Invite to use MeisterSystems", {
              ns: "OrganisationInvite",
            })
          }
          handleClose={handleClose}
          actionButtons={[
            {
              label: t("Cancel", {
                ns: "Global",
              }),
              handleClick: handleClose,
              buttonProps: { variant: "text" },
            },
            {
              label: t("Invite", {
                ns: "Global",
              }),
              buttonProps: {
                type: "submit",
                form: formId,
                disabled: !formikProps.isValid,
                loading: formikProps.isSubmitting,
              },
            },
          ]}
          isLoading={isLoading}
        >
          <Form id={formId}>{formFields}</Form>
        </Modal>
      )}
    </Formik>
  );
};
